// utils/isDateLocked.ts

import { Timestamp } from 'firebase/firestore';
import { currentWeek } from './currentWeek';

/**
 * Returns every valid key for a week.
 *
 * The first key uses the correct ISO week-year. The additional keys preserve
 * compatibility with historical values generated using Date.getFullYear().
 */
const getCompatibleWeekKeys = (date: Date): string[] => {
  const weekNumber = currentWeek(date);

  // ISO weeks belong to the year containing the week's Thursday.
  const thursday = new Date(date);
  thursday.setHours(0, 0, 0, 0);
  thursday.setDate(thursday.getDate() + 3 - ((thursday.getDay() + 6) % 7));

  const isoWeekYear = thursday.getFullYear();

  // Find the calendar years touched by this Monday–Sunday week.
  const weekStart = new Date(date);
  weekStart.setHours(0, 0, 0, 0);

  const day = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() + (day === 0 ? -6 : 1 - day));

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return Array.from(
    new Set([
      `${weekNumber}-${isoWeekYear}`,
      `${weekNumber}-${weekStart.getFullYear()}`,
      `${weekNumber}-${weekEnd.getFullYear()}`,
    ]),
  );
};

/**
 * Determines if a given date is locked based on approved weeks,
 * the user's start date, the company's start date, and a special locked date.
 */
export const isDateLocked = (
  dateObj: Date,
  approvedWeeks: string[],
  userStartDate?: Timestamp,
  companyStartDate?: Date,
): boolean => {
  // Lock any date before August 5, 2024.
  const specialLockedDate = new Date('2024-08-05');

  if (dateObj < specialLockedDate) {
    return true;
  }

  // Recognize both canonical and historical week-key formats.
  const compatibleWeekKeys = getCompatibleWeekKeys(dateObj);

  const isApproved = compatibleWeekKeys.some((weekKey) =>
    approvedWeeks?.includes(weekKey),
  );

  if (isApproved) {
    return true;
  }

  // Lock dates before the later of the user and company start dates.
  let lockDate: Date | undefined;

  if (userStartDate && companyStartDate) {
    const userStart = userStartDate.toDate();
    lockDate = userStart > companyStartDate ? userStart : companyStartDate;
  } else if (companyStartDate) {
    lockDate = companyStartDate;
  } else if (userStartDate) {
    lockDate = userStartDate.toDate();
  }

  if (lockDate && dateObj < lockDate) {
    return true;
  }

  return false;
};
